/**
 * CORE-883 — счётчик непрочитанных уведомлений для шапки.
 *
 * Отдельным хуком, а не запросом внутри шапки: шапка живёт на каждом экране, и
 * запрос, вписанный прямо в неё, ушёл бы в бесконечный цикл при первой же
 * неаккуратной зависимости. Здесь всё, что можно сделать неправильно, сделано
 * один раз.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { api } from '../services/api';

/** Как часто переспрашиваем. Уведомления не чат — минуты здесь уместнее секунд. */
const POLL_MS = 60_000;

// CORE-1386 — один запрос в полёте на всё окно (веб). Шапка есть на каждом
// экране, и на экране уведомлений её счётчик и счётчик самого экрана уходили в
// сеть одновременно. Кеша по времени нет намеренно: после «прочитать всё» бейдж
// должен обновиться первым же запросом, а не через минуту.
let inflight: Promise<{ count: number }> | null = null;

export function fetchUnreadNotificationsCount(): Promise<{ count: number }> {
  // На телефоне у счётчика один потребитель — там как было, напрямую.
  if (Platform.OS !== 'web') return api.getNotificationsUnreadCount();
  if (inflight) return inflight;
  const request = api.getNotificationsUnreadCount().finally(() => {
    if (inflight === request) inflight = null;
  });
  inflight = request;
  return request;
}

// «Прочитать все» и открытие уведомления: экран уведомлений сообщает об этом
// сам. Иначе колокольчик ждал минутного опроса или пересоздания своего экрана —
// на телефоне профиль мастера остаётся смонтированным под «Уведомлениями».
const listeners = new Set<() => void>();

/** Перечитать счётчик во всех смонтированных колокольчиках — сразу, без опроса. */
export function notifyUnreadNotificationsChanged(): void {
  for (const listener of listeners) listener();
}

export function useUnreadNotifications(isAuthenticated: boolean): number {
  const [count, setCount] = useState(0);
  const alive = useRef(true);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) { setCount(0); return; }
    try {
      const res = await fetchUnreadNotificationsCount();
      // Экран мог размонтироваться, пока ответ шёл: писать в стейт после этого
      // значит получить предупреждение и утечку.
      if (alive.current) setCount(res?.count ?? 0);
    } catch {
      // Молча: счётчик — украшение, и его сбой не должен ничего показывать
      // человеку. Прошлое значение остаётся.
    }
  }, [isAuthenticated]);

  useEffect(() => {
    alive.current = true;
    void refresh();
    const timer = setInterval(() => { void refresh(); }, POLL_MS);
    const onChanged = () => { void refresh(); };
    listeners.add(onChanged);
    return () => {
      alive.current = false;
      clearInterval(timer);
      listeners.delete(onChanged);
    };
  }, [refresh]);

  return count;
}
